import * as React from "react"
import { cn } from "../../../lib/utils"
import { Button } from "../button/button"
import { Icon } from "../icon/icon"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../tooltip/tooltip"
import { IndeterminateProgressBar } from "./indeterminate-progress-bar"
import { DeterminateProgressBar } from "./determinate-progress-bar"
import { JobProgressIndicator } from "./job-progress-indicator"
import { SegmentProgressBar } from "./segment-progress-bar"
import {
  jobActivityPanelAnchorClassName,
  panelEnterClassName,
  panelHoverClassName,
  panelIconActionClassName,
  panelInlineWidthClassName,
  panelDockTranslateClassName,
  panelSurfaceClassName,
} from "./job-activity-panel-classes"
import { JobActivityPortal } from "./job-activity-portal"
import { motion, usePanelDockShift } from "./panel-dock"
import type { IconName } from "../icon/icon-registry"

export { jobActivityPanelAnchorClassName } from "./job-activity-panel-classes"

const COUNT_LINE_CLASS = "text-sm tabular-nums text-muted-foreground"

export interface JobActivityPanelProps {
  /** Primary heading (e.g. "Generating scripts…"). */
  title: string
  /** Shown under the large count, e.g. "of 50 scripts". */
  totalLabel?: string
  /** Second count line under the primary count, e.g. "1 of 2 briefs". */
  secondaryLabel?: string
  completed?: number
  total?: number
  subtitle?: string | null
  /** Optional step label shown during multi-phase jobs (e.g. "Step 2 of 6"). */
  stepLabel?: string | null

  progressPercent: number | null
  isCancelling?: boolean
  indicatorIcon?: IconName

  /** When set with `total`, renders segmented progress; otherwise indeterminate bar. */
  segmentSize?: number
  /** When true, hides the progress bar row (use with `stepLabel` for prep phases). */
  hideProgressBar?: boolean

  /** Dot-separated stats row below the bar. */
  stats?: React.ReactNode
  /** Optional checklist or other body content between header and progress footer. */
  checklist?: React.ReactNode

  minimized?: boolean
  onMinimizedChange?: (minimized: boolean) => void
  /** Chip label when minimized (e.g. "12 / 50"). */
  minimizedLabel?: string

  minimizeAriaLabel: string
  expandAriaLabel: string
  minimizeTitle?: string

  onStop?: () => void
  /** Accessible name for the header stop control. */
  stopAriaLabel?: string
  /** Hover tooltip for the stop control. */
  stopTitle?: string
  /** Fallback accessible name when `stopAriaLabel` is omitted. */
  stopLabel?: string

  /**
   * Clears the card without touching the job. Offer it only when the job cannot
   * be stopped — otherwise a user orphans work they could have aborted.
   */
  onDismiss?: () => void
  /** Accessible name and tooltip for the header dismiss control. */
  dismissLabel?: string

  /** progressbar */
  ariaLabel: string
  ariaValueText?: string
  /**
   * Numeric progressbar scale. Defaults to `completed` / `total`.
   * Pass a combined scale when the visible count is a subset of the bar
   * (e.g. scripts hero vs scripts+briefs bar).
   */
  ariaValueNow?: number
  ariaValueMax?: number

  /** When provided, the title becomes a clickable link (e.g. navigate to the task's source page). */
  onTitleClick?: () => void
  /** Small label shown above the title (e.g. tenant name). */
  tenantName?: string
  /** Who started the job, shown under the title (already localized). */
  triggeredBy?: string

  className?: string

  /** When true, render inline (no portal, no fixed positioning). */
  disablePortal?: boolean
}

function HeaderIconTooltip({
  label,
  children,
}: {
  label: string
  children: React.ReactElement
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent side="top">{label}</TooltipContent>
    </Tooltip>
  )
}

export function JobActivityPanel({
  title,
  totalLabel,
  secondaryLabel,
  completed = 0,
  total = 0,
  subtitle,
  stepLabel,
  progressPercent,
  isCancelling = false,
  indicatorIcon,
  segmentSize = 10,
  hideProgressBar = false,
  stats,
  checklist,
  minimized = false,
  onMinimizedChange,
  minimizedLabel,
  minimizeAriaLabel,
  expandAriaLabel,
  minimizeTitle,
  onStop,
  stopAriaLabel,
  stopTitle,
  stopLabel,
  onDismiss,
  dismissLabel,
  onTitleClick,
  tenantName,
  triggeredBy,
  ariaLabel,
  ariaValueText,
  ariaValueNow,
  ariaValueMax,
  className,
  disablePortal = false,
}: JobActivityPanelProps) {
  const hasTotals = total > 0
  const valueNow = ariaValueNow ?? completed
  const valueMax = ariaValueMax ?? (hasTotals ? total : 1)
  const showSegmentBar = hasTotals && segmentSize > 0
  const showDeterminateBar = !showSegmentBar && progressPercent != null
  const showProgressBar = !hideProgressBar
  const showFooter = showProgressBar || Boolean(stats)
  const showChecklist = Boolean(checklist)

  const dockRef = React.useRef<HTMLDivElement>(null)
  const dockX = usePanelDockShift(dockRef)

  const indicator = (
    <JobProgressIndicator
      progressPercent={progressPercent}
      isCancelling={isCancelling}
      icon={indicatorIcon}
    />
  )

  const Wrapper = disablePortal ? React.Fragment : JobActivityPortal
  const anchorClass = disablePortal ? "" : jobActivityPanelAnchorClassName

  if (minimized) {
    return (
      <Wrapper>
        <button
          type="button"
          onClick={() => onMinimizedChange?.(false)}
          aria-label={expandAriaLabel}
          data-slot="job-activity-panel"
          data-state="minimized"
          className={cn(
            anchorClass,
            panelSurfaceClassName,
            panelEnterClassName,
            "flex w-auto max-w-none items-center gap-2.5 py-2 pl-2.5 pr-3",
            "transition-shadow hover:shadow-2xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50",
            className,
          )}
        >
          {indicator}
          <span className="text-sm font-medium tabular-nums text-foreground">
            {minimizedLabel ?? title}
          </span>
          <Icon name="chevron-up" size="xs" className="text-muted-foreground" />
        </button>
      </Wrapper>
    )
  }

  return (
    <Wrapper>
      <motion.div
        ref={dockRef}
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={valueMax}
        aria-valuenow={valueNow}
        aria-busy="true"
        aria-valuetext={ariaValueText}
        aria-label={ariaLabel}
        data-slot="job-activity-panel"
        data-state={isCancelling ? "cancelling" : "active"}
        style={
          disablePortal
            ? ({ ["--panel-dock-x" as string]: dockX } as React.ComponentProps<
                typeof motion.div
              >["style"])
            : undefined
        }
        className={cn(
          anchorClass,
          disablePortal && panelInlineWidthClassName,
          disablePortal && panelDockTranslateClassName,
          panelEnterClassName,
          isCancelling && "opacity-90",
          className,
        )}
      >
      <div className={cn(panelSurfaceClassName, panelHoverClassName)}>
      <div
        className={cn(
          "flex items-start gap-3 px-4 pt-3.5",
          showFooter || showChecklist ? "pb-2" : "pb-3.5",
        )}
      >
        <div className="mt-0.5">{indicator}</div>

        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          {tenantName && (
            <span className="block truncate text-[10px] leading-none text-muted-foreground/70 mb-0.5">{tenantName}</span>
          )}
          {onTitleClick ? (
            <button
              type="button"
              onClick={onTitleClick}
              title={title}
              className="block w-full min-w-0 cursor-pointer truncate rounded-sm text-left text-sm font-semibold text-foreground transition-colors duration-150 hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
            >
              {title}
            </button>
          ) : (
            <span className="block truncate text-sm font-semibold text-foreground" title={title}>
              {title}
            </span>
          )}
          {triggeredBy ? (
            <span className="block truncate text-[11px] text-muted-foreground" title={triggeredBy}>
              {triggeredBy}
            </span>
          ) : null}
          {hasTotals && secondaryLabel ? (
            <span className={COUNT_LINE_CLASS}>
              {totalLabel ? `${completed} ${totalLabel}` : completed}
            </span>
          ) : hasTotals ? (
            <p className="flex items-baseline gap-1 tabular-nums">
              <span className="text-2xl font-semibold tracking-tight text-foreground">{completed}</span>
              {totalLabel ? (
                <span className="text-sm text-muted-foreground">{totalLabel}</span>
              ) : null}
            </p>
          ) : null}
          {secondaryLabel ? (
            <span className={COUNT_LINE_CLASS}>{secondaryLabel}</span>
          ) : null}
          {stepLabel ? (
            <span className="text-[11px] font-medium tabular-nums tracking-wide text-muted-foreground">
              {stepLabel}
            </span>
          ) : null}
          {subtitle ? (
            <span className="text-[11px] leading-snug text-muted-foreground">{subtitle}</span>
          ) : null}
        </div>

        {onMinimizedChange || onStop || onDismiss ? (
          <TooltipProvider delayDuration={300}>
            <div className="-mr-0.5 -mt-0.5 flex shrink-0 items-center gap-1">
              {onStop ? (
                <HeaderIconTooltip label={stopTitle ?? stopAriaLabel ?? stopLabel ?? "Stop"}>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  disabled={isCancelling}
                  onClick={onStop}
                  aria-label={stopAriaLabel ?? stopLabel ?? "Stop"}
                  className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                >
                  {isCancelling ? (
                    <Icon name="loader" size="xs" className="animate-spin" color="inherit" />
                  ) : (
                    <Icon name="square" size="xs" color="inherit" />
                  )}
                </Button>
                </HeaderIconTooltip>
              ) : null}
              {onMinimizedChange ? (
                <HeaderIconTooltip label={minimizeTitle ?? minimizeAriaLabel}>
                  <button
                    type="button"
                    onClick={() => onMinimizedChange(true)}
                    aria-label={minimizeAriaLabel}
                    className="inline-flex min-h-[44px] min-w-[44px] cursor-pointer items-center justify-center rounded-md text-muted-foreground transition-colors duration-150 hover:bg-background hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
                  >
                    <Icon name="chevron-down" size="xs" />
                  </button>
                </HeaderIconTooltip>
              ) : null}
              {onDismiss ? (
                <HeaderIconTooltip label={dismissLabel ?? "Dismiss"}>
                  <button
                    type="button"
                    onClick={onDismiss}
                    aria-label={dismissLabel ?? "Dismiss"}
                    className={panelIconActionClassName}
                  >
                    <Icon name="x" size="xs" color="inherit" />
                  </button>
                </HeaderIconTooltip>
              ) : null}
            </div>
          </TooltipProvider>
        ) : null}
      </div>

      {showChecklist ? <div className="px-4 pb-3">{checklist}</div> : null}

      {showFooter ? (
      <div className={cn("space-y-2.5 px-4 pb-3", isCancelling && "opacity-60")}>
        {showProgressBar ? (
          showSegmentBar ? (
          <SegmentProgressBar
            completed={completed}
            total={total}
            segmentSize={segmentSize}
            isPaused={isCancelling}
          />
        ) : showDeterminateBar ? (
          <DeterminateProgressBar percent={progressPercent!} isPaused={isCancelling} />
        ) : (
          <IndeterminateProgressBar isPaused={isCancelling} />
        )
        ) : null}
        {stats}
      </div>
      ) : null}
      </div>
    </motion.div>
    </Wrapper>
  )
}
